import { css, cx, etiColors, resetButtonStyle } from '@eti/styles';
import { breakpoints, Inline, vars } from '@etg/wings';
import type { ReactNode } from 'react';
import { useState } from 'react';
import ScrollableTabs from '../../../widgets/tabs/components/ScrollableTabs';
import { focusStyles } from '../../../utils/styleUtils';

export type TabItemsType = {
  content: ReactNode;
  title: NonNullable<ReactNode>;
};

interface TabsProps {
  id: string;
  initiallyOpenIndex?: number;
  tabItems: TabItemsType[];
}

const wrapperStyles = css`
  background: ${etiColors.white};
  border-radius: inherit;
  overflow: hidden;
`;

const contentWrapperStyles = css`
  padding: 16px;

  @media (min-width: ${breakpoints._768}) {
    padding: 24px;
  }
`;

const titleWrapperStyles = css`
  box-shadow: 0 1px 2px rgba(0 0 0/ 1%);
`;

const tabStyles = (active: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  background-color: ${etiColors.white};
  border-bottom: 2px solid ${active ? vars.colors.inputs.main : vars.colors.divider};
  color: ${active ? vars.colors.inputs.main : '#737373'};
  cursor: ${!active && 'pointer'};
  flex: 1;
  font-weight: 700;
  outline: 2px solid transparent;
  padding: 16px 8px;

  &:hover {
    background-color: ${vars.colors.inputs.hover};
    color: ${vars.colors.inputs.main};
  }

  &:focus-visible {
    outline-offset: -0.1875rem;
  }

  &:first-child {
    border-top-left-radius: ${vars.card.borderRadius};
  }

  &:last-child {
    border-top-right-radius: ${vars.card.borderRadius};
  }
`;

const Tabs = ({ id, initiallyOpenIndex, tabItems }: TabsProps) => {
  const [activeIndex, setActiveIndex] = useState(initiallyOpenIndex || 0);

  return (
    <div className={wrapperStyles} data-testid="tabs-wrapper">
      <Inline className={titleWrapperStyles} data-testid="tabs-titles-wrapper" spacing={4}>
        <ScrollableTabs role="tablist" selectedIndex={activeIndex}>
          <>
            {tabItems.map((tab, index) => (
              <ScrollableTabs.Tab
                aria-controls={`${id}-tab-panel-${index}`}
                aria-selected={activeIndex === index}
                as="button"
                className={cx(focusStyles, tabStyles(activeIndex === index))}
                data-testid={`tab-title-${index}`}
                id={`${id}-tab-${index}`}
                index={index}
                key={`${id}-tab-title-${index}`}
                onClick={() => setActiveIndex(index)}
                role="tab"
                type="button"
              >
                {tab.title}
              </ScrollableTabs.Tab>
            ))}
          </>
        </ScrollableTabs>
      </Inline>
      {activeIndex !== null && (
        <div
          aria-labelledby={`${id}-tab-${activeIndex}`}
          className={contentWrapperStyles}
          data-testid={`tab-content-${activeIndex}`}
          id={`${id}-tab-panel-${activeIndex}`}
          key={`${id}-tab-panel-${activeIndex}`}
          role="tabpanel"
        >
          {tabItems[activeIndex].content}
        </div>
      )}
    </div>
  );
};

export default Tabs;
